/**
 * Paper2Ink Studio - Main Application Entrypoint
 * Mounts modular TypeScript packages:
 * - CIM v1 Data Contracts & Validation
 * - CaptureManager & Quality Diagnostics
 * - Command Pattern & Lossless History
 * - BranchWorkflowManager (Mask Rebuild Preservation)
 * - Selection & SVG Vector Rendering
 * - Review Declarations & Hash Binding
 * - IndexedDB DraftStore (Offline Crash Recovery)
 * - Pure Vector SVG & Transactional Open
 */

import {
  type CIMDocument,
  type InkObject,
  type CompoundFill,
  type CenterlineStroke,
  type UserDeclaration,
  validateCIMDocument,
  createBlankCIMDocument,
} from './editor/model.ts';
import { renderObjectToSVGPath } from './editor/render.ts';
import { clampZoom, fitContent } from './editor/camera.ts';
import {
  BranchWorkflowManager,
  type RevectorizationResult,
} from './editor/branchWorkflow.ts';
import {
  MoveObjectsCommand,
  RecolorObjectsCommand,
  ScaleObjectsCommand,
  RotateObjectsCommand,
  DeleteObjectsCommand,
  AddObjectsCommand,
} from './editor/commands.ts';
import { importSVG } from './editor/svgImport.ts';
import {
  buildTransferredDocument,
  shouldShowBlankCanvasOption,
} from './editor/transfer.ts';
import { computeObjectBounds } from './editor/selection.ts';
import {
  createUserDeclaration,
  verifyUserDeclaration,
} from './review/declaration.ts';
import {
  exportToSVG,
  exportToJSON,
  openDocument,
} from './export/index.ts';
import { DraftStore } from './storage/index.ts';
import { CaptureManager } from './capture/index.ts';
import { SourceMarquee, cropDataUrlToBlob } from './capture/sourceMarquee.ts';
import type { Rect } from './capture/regionCrop.ts';
import { expandRectToMinSide } from './capture/regionCrop.ts';
import { InkMaskReview } from './capture/inkMaskReview.ts';
import {
  luminanceFromImageData,
  proposeRegionsFromLuminance,
  scaleRectToSource,
} from './capture/regionPropose.ts';
import {
  createApiClient,
  AuthLibraryManager,
  ApiClientError,
  type CloudOpenResult,
} from './api/index.ts';
import {
  JobManager,
  JobUI,
  type PendingJobRecord,
} from './jobs/index.ts';
import {
  DocumentSyncManager,
  SyncConflictUI,
} from './editor/sync.ts';
import {
  canDeleteSource,
  executeSourceDeletion,
  SOURCE_DELETE_CONFIRMATION_MSG,
} from './editor/sourceController.ts';
import { TabletPointerController } from './editor/pointerController.ts';
import { NotebookModeController, type AppMode } from './editor/notebookMode.ts';

// Global application state for UI and automated testing inspection
export interface AppState {
  source: string | null;
  sourceAssetId?: string | null;
  width: number;
  height: number;
  inputQuality: any;
  objects: InkObject[];
  review: {
    method?: string;
    userReview?: {
      confirmed: boolean;
      hash?: string;
      time?: string;
    };
    [key: string]: any;
  };
  activeBranch: string;
  branches: string[];
  selectedIds: Set<string>;
  corrected: string | null;
  automatic: string | null;
  draftRecovered: boolean;
}

const state: AppState = {
  source: null,
  sourceAssetId: null,
  width: 0,
  height: 0,
  inputQuality: null,
  objects: [],
  review: {
    userReview: {
      confirmed: false,
    },
  },
  activeBranch: 'main',
  branches: ['main'],
  selectedIds: new Set(),
  corrected: null,
  automatic: null,
  draftRecovered: false,
};

let busy = false;
let showSource = false;
let branchWorkflow: BranchWorkflowManager | null = null;
const draftStore = new DraftStore('default');
const captureManager = new CaptureManager();
const sourceByAssetId = new Map<string, string>();
const pathToElementMap = new Map<string, SVGPathElement>();
let autoSaveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let stageZoom = 1;

function scheduleDebouncedAutoSave(delayMs = 500): void {
  if (autoSaveDebounceTimer !== null) {
    clearTimeout(autoSaveDebounceTimer);
  }
  autoSaveDebounceTimer = setTimeout(() => {
    autoSaveDebounceTimer = null;
    void autoSaveDraft();
  }, delayMs);
}

// Expose state and flags to window for testing & debugging
(window as any).state = state;
(window as any).busy = false;
(window as any).showSource = false;
(window as any).__PAPER2INK_APP__ = {
  getWorkflow: () => branchWorkflow,
  getState: () => state,
  getDraftStore: () => draftStore,
  exportToSVG: () => branchWorkflow ? exportToSVG(branchWorkflow.getActiveDocument()) : null,
  exportToJSON: () => branchWorkflow ? exportToJSON(branchWorkflow.getActiveDocument()) : null,
  openDocument: async (jsonStr: string) => handleOpenJson(jsonStr),
  updateSourceControls: () => updateSourceControls(),
  restoreSourceForDocument: async (doc: CIMDocument) => restoreSourceForDocument(doc),
  clearRenderedSource: () => clearRenderedSource(),
  deleteSource: async () => {
    if (deleteSourceBtn && !deleteSourceBtn.disabled) {
      deleteSourceBtn.click();
    } else if (authLibrary && authLibrary.getCurrentDocId()) {
      await authLibrary.deleteSource(authLibrary.getCurrentDocId()!);
      clearRenderedSource();
    }
  },
  getPointerController: () => pointerController,
  getNotebookModeController: () => notebookController,
};

function setBusy(val: boolean) {
  busy = val;
  (window as any).busy = val;
}

// DOM Elements
const photoInput = document.getElementById('photo') as HTMLInputElement;
const methodSelect = document.getElementById('method') as HTMLSelectElement;
const qualityBtn = document.getElementById('quality') as HTMLButtonElement;
const convertBtn = document.getElementById('convert') as HTMLButtonElement;
const qualityOverride = document.getElementById('qualityOverride') as HTMLInputElement;
const qualityStatus = document.getElementById('qualityStatus') as HTMLDivElement;
const statusDiv = document.getElementById('status') as HTMLDivElement;
const reviewDiv = document.getElementById('review') as HTMLDivElement;
const recoveryNotice = document.getElementById('recoveryNotice') as HTMLDivElement;
const recoveryMsg = document.getElementById('recoveryMsg') as HTMLSpanElement;
const recoverDraftBtn = document.getElementById('recoverDraftBtn') as HTMLButtonElement;
const discardDraftBtn = document.getElementById('discardDraftBtn') as HTMLButtonElement;
const checkDots = document.getElementById('checkDots') as HTMLInputElement;
const checkSigns = document.getElementById('checkSigns') as HTMLInputElement;
const checkLabels = document.getElementById('checkLabels') as HTMLInputElement;
const reviewConfirmBtn = document.getElementById('reviewConfirm') as HTMLButtonElement;
const toolSelect = document.getElementById('tool') as HTMLSelectElement;
const colorInput = document.getElementById('color') as HTMLInputElement;
const widthInput = document.getElementById('width') as HTMLInputElement;
const recolorBtn = (document.getElementById('recolor') || document.getElementById('recolorBtn')) as HTMLButtonElement;
const scaleBtn = (document.getElementById('scale') || document.getElementById('scaleBtn')) as HTMLButtonElement;
const rotateBtn = document.getElementById('rotate') as HTMLButtonElement | null;
const transferSelectionBtn = document.getElementById('transferSelectionBtn') as HTMLButtonElement | null;
const selectionActionBar = document.getElementById('selectionActionBar') as HTMLDivElement | null;
const selectionCountLabel = document.getElementById('selectionCountLabel') as HTMLSpanElement | null;
const blankCanvasBtn = document.getElementById('blankCanvasBtn') as HTMLButtonElement | null;
const clearSelectionBtn = document.getElementById('clearSelectionBtn') as HTMLButtonElement | null;
const svgImportInput = document.getElementById('svgImport') as HTMLInputElement | null;
const groupBtn = (document.getElementById('group') || document.getElementById('groupBtn')) as HTMLButtonElement;
const ungroupBtn = (document.getElementById('ungroup') || document.getElementById('ungroupBtn')) as HTMLButtonElement;
const deleteBtn = (document.getElementById('delete') || document.getElementById('deleteBtn')) as HTMLButtonElement;
const undoBtn = (document.getElementById('undo') || document.getElementById('undoBtn')) as HTMLButtonElement;
const redoBtn = (document.getElementById('redo') || document.getElementById('redoBtn')) as HTMLButtonElement;
const rebuildBtn = document.getElementById('rebuild') as HTMLButtonElement;
const sourceToggleBtn = document.getElementById('sourceToggle') as HTMLButtonElement;
const branchSelect = document.getElementById('branchSelect') as HTMLSelectElement;
const branchInfo = document.getElementById('branchInfo') as HTMLDivElement;
const saveBtn = (document.getElementById('save') || document.getElementById('saveBtn')) as HTMLButtonElement;
const openFileInput = (document.getElementById('open') || document.getElementById('openFileInput')) as HTMLInputElement;
const openBtn = document.getElementById('openBtn') as HTMLButtonElement | null;
const exportSvgBtn = (document.getElementById('export') || document.getElementById('exportSvgBtn')) as HTMLButtonElement;
const objectCountDisplay = document.getElementById('objectCountDisplay') as HTMLSpanElement;
const selectionInfo = document.getElementById('selectionInfo') as HTMLSpanElement;
const sourceDimensions = document.getElementById('sourceDimensions') as HTMLSpanElement;
const sourceCanvas = document.getElementById('sourceCanvas') as HTMLCanvasElement;
const sourcePreview = document.getElementById('sourcePreview') as HTMLImageElement;
const deleteSourceBtn = document.getElementById('deleteSourceBtn') as HTMLButtonElement | null;
const sourceRegionBtn = document.getElementById('sourceRegionBtn') as HTMLButtonElement | null;
const sourceRegionConvertBtn = document.getElementById('sourceRegionConvertBtn') as HTMLButtonElement | null;
const inkPreviewBtn = document.getElementById('inkPreviewBtn') as HTMLButtonElement | null;
const sourceProposeBtn = document.getElementById('sourceProposeBtn') as HTMLButtonElement | null;
const inkPreviewCanvas = document.getElementById('inkPreviewCanvas') as HTMLCanvasElement | null;
const sourceCard = document.getElementById('sourceCard') as HTMLDivElement | null;
const stage = document.getElementById('stage') as HTMLDivElement;
const stageWrapper = document.getElementById('stageWrapper') as HTMLDivElement;
const inkSvg = document.getElementById('ink') as unknown as SVGSVGElement;
const maskCanvas = (document.getElementById('mask') || document.getElementById('maskCanvas')) as HTMLCanvasElement;

// Auth and Library DOM Elements (SAAS-312)
const authStatus = document.getElementById('authStatus') as HTMLSpanElement;
const loginBtn = document.getElementById('loginBtn') as HTMLButtonElement;
const logoutBtn = document.getElementById('logoutBtn') as HTMLButtonElement;
const openLibraryBtn = document.getElementById('openLibraryBtn') as HTMLButtonElement;
const saveCloudBtn = document.getElementById('saveCloudBtn') as HTMLButtonElement;
const sessionExpiredNotice = document.getElementById('sessionExpiredNotice') as HTMLDivElement;
const sessionExpiredMsg = document.getElementById('sessionExpiredMsg') as HTMLSpanElement;
const fallbackDownloadBtn = document.getElementById('fallbackDownloadBtn') as HTMLButtonElement;
const reloginBtn = document.getElementById('reloginBtn') as HTMLButtonElement;
const libraryModal = document.getElementById('libraryModal') as HTMLDivElement;
const closeLibraryBtn = document.getElementById('closeLibraryBtn') as HTMLButtonElement;
const newDocTitleInput = document.getElementById('newDocTitleInput') as HTMLInputElement;
const createNewDocBtn = document.getElementById('createNewDocBtn') as HTMLButtonElement;
const libraryDocList = document.getElementById('libraryDocList') as HTMLDivElement;

// Mode & Notebook DOM Elements (§Top-level modes, §Defter workspace)
const tabConvert = document.getElementById('tabConvert') as HTMLElement | null;
const tabNotebook = document.getElementById('tabNotebook') as HTMLElement | null;
const notebookToolbar = document.getElementById('notebookToolbar') as HTMLElement | null;
const notebookDocTitle = document.getElementById('notebookDocTitle') as HTMLElement | null;
const notebookSyncBadge = document.getElementById('notebookSyncBadge') as HTMLElement | null;
const notebookBranchBadge = document.getElementById('notebookBranchBadge') as HTMLElement | null;
const notebookSelectionInfo = document.getElementById('notebookSelectionInfo') as HTMLElement | null;
const notebookLibraryBtn = document.getElementById('notebookLibraryBtn') as HTMLButtonElement | null;
const notebookToolSelect = document.getElementById('notebookToolSelect') as HTMLButtonElement | null;
const notebookToolLasso = document.getElementById('notebookToolLasso') as HTMLButtonElement | null;
const notebookToolPen = document.getElementById('notebookToolPen') as HTMLButtonElement | null;
const notebookToolEraser = document.getElementById('notebookToolEraser') as HTMLButtonElement | null;
const notebookColor = document.getElementById('notebookColor') as HTMLInputElement | null;
const notebookWidth = document.getElementById('notebookWidth') as HTMLInputElement | null;
const notebookUndo = document.getElementById('notebookUndo') as HTMLButtonElement | null;
const notebookRedo = document.getElementById('notebookRedo') as HTMLButtonElement | null;
const notebookSaveBtn = document.getElementById('notebookSaveBtn') as HTMLButtonElement | null;
const notebookExportBtn = document.getElementById('notebookExportBtn') as HTMLButtonElement | null;
const notebookAddBlankBtn = document.getElementById('notebookAddBlankBtn') as HTMLButtonElement | null;
const notebookTransferBtn = document.getElementById('notebookTransferBtn') as HTMLButtonElement | null;

let notebookController: NotebookModeController | null = null;
let pointerController: TabletPointerController | null = null;

// Initialize API client & AuthLibraryManager
const apiClient = createApiClient();
const authLibrary = new AuthLibraryManager({
  api: apiClient,
  draftStore,
  onSessionExpired: (_doc, _fallbackJson) => {
    sessionExpiredNotice.style.display = 'flex';
    sessionExpiredMsg.textContent = 'Oturum süresi doldu. Taslağınız yerel hafızada güvenle saklandı.';
  },
  onStatusChange: (msg) => {
    statusDiv.textContent = msg;
  },
});

(window as any).__PAPER2INK_APP__.getApiClient = () => apiClient;
(window as any).__PAPER2INK_APP__.getAuthLibrary = () => authLibrary;

// Jobs Manager and UI (SAAS-410)
const jobStatusBtn = document.getElementById('jobStatusBtn') as HTMLButtonElement | null;
const jobManager = new JobManager(apiClient);

const jobUI = new JobUI({
  onCancel: async (jobId) => {
    try {
      await jobManager.cancelJob(jobId);
      statusDiv.textContent = 'Dönüştürme işi iptal edildi.';
    } catch (err: any) {
      statusDiv.textContent = `İptal hatası: ${err.message}`;
    }
  },
  onRetry: async (jobId) => {
    try {
      await jobManager.retryJob(jobId);
      statusDiv.textContent = 'Dönüştürme işi yeniden başlatıldı.';
    } catch (err: any) {
      statusDiv.textContent = `Yeniden deneme hatası: ${err.message}`;
    }
  },
  onApplyResult: async (job: PendingJobRecord) => {
    try {
      authLibrary.setCurrentDocId(job.documentId);
      if (job.resultRevisionId) {
        authLibrary.setCurrentHeadRevisionId(job.resultRevisionId);
      }
      if (!branchWorkflow) {
        const { cimDocument } = await jobManager.fetchResultCIMDocument(job);
        branchWorkflow = new BranchWorkflowManager(cimDocument);
        currentDocumentTitle = 'Dönüştürülen Çizim';
        await syncUIFromDocument(cimDocument);
      } else {
        const applied = await jobManager.applyResultToWorkflow(branchWorkflow, job);
        currentDocumentTitle = 'Dönüştürülen Çizim';
        await syncUIFromDocument(applied.document);
      }
      initSyncManager(job.documentId, job.resultRevisionId);
      statusDiv.textContent = 'Dönüştürme sonucu başarıyla uygulandı.';
      await autoSaveDraft();
      updateSourceControls();
    } catch (err: any) {
      statusDiv.textContent = `Sonuç uygulama hatası: ${err.message}`;
    }
  },
});

jobManager.onJobUpdate((job) => {
  jobUI.update(job);
  if (jobStatusBtn) {
    jobStatusBtn.style.display = 'inline-block';
    jobStatusBtn.textContent = `İş: ${job.state}`;
  }
});

if (jobStatusBtn) {
  jobStatusBtn.addEventListener('click', () => {
    const job = jobManager.getActiveJob();
    if (job) {
      jobUI.show(job);
    }
  });
}

(window as any).__PAPER2INK_APP__.getJobManager = () => jobManager;
(window as any).__PAPER2INK_APP__.getJobUI = () => jobUI;

// Document Synchronization and Conflict Resolution (SAAS-411)
let syncManager: DocumentSyncManager | null = null;
let syncConflictUI: SyncConflictUI | null = null;

function initSyncManager(docId: string, headRevId?: string | null) {
  if (syncManager) {
    syncManager.destroy();
  }
  if (!branchWorkflow) return null;

  syncManager = new DocumentSyncManager({
    api: apiClient,
    draftStore,
    workflow: branchWorkflow,
    documentId: docId,
    initialHeadRevisionId: headRevId || null,
    onStateChange: (syncState) => {
      if (syncState === 'conflict') {
        statusDiv.textContent = 'Kaydetme çatışması (409): Belge sunucuda güncellenmiş.';
      }
      if (notebookController) {
        notebookController.updateDocumentHeader({
          syncState: syncState as any,
        });
      }
    },
    onStatusMessage: (msg) => {
      statusDiv.textContent = msg;
    },
    onSessionExpired: (_doc, _fallbackJson) => {
      sessionExpiredNotice.style.display = 'flex';
      sessionExpiredMsg.textContent = 'Oturum süresi doldu. Taslağınız yerel hafızada güvenle saklandı.';
    },
  });

  syncConflictUI = new SyncConflictUI(syncManager, document.body, {
    onConflictResolved: async (result) => {
      if (result.action === 'fork' || result.action === 'pull') {
        if (branchWorkflow) {
          syncUIFromDocument(branchWorkflow.getActiveDocument());
        }
      }
      if (result.action === 'fork' && result.branchName) {
        statusDiv.textContent = `Çatışma dalı oluşturuldu: ${result.branchName}. Bu taslak ayrı olarak kaydedildi.`;
      } else if (result.action === 'pull') {
        statusDiv.textContent = `Sunucudaki güncel revizyon uygulandı.`;
      } else if (result.action === 'download' && result.filename) {
        statusDiv.textContent = `Çatışan taslak JSON dosyası olarak indirildi: ${result.filename}`;
      }
    },
    onNotice: (msg) => {
      statusDiv.textContent = msg;
    },
  });
  return syncManager;
}

(window as any).__PAPER2INK_APP__.getSyncManager = () => syncManager;
(window as any).__PAPER2INK_APP__.initSyncManager = initSyncManager;

let currentDocumentTitle = 'Başlıksız Belge';
let lastDragEndTime = 0;

if (tabConvert && tabNotebook) {
  notebookController = new NotebookModeController(
    {
      tabConvert,
      tabNotebook,
      notebookToolbar,
      bodyElement: document.body,
      notebookDocTitle,
      notebookSyncBadge,
      notebookBranchBadge,
      notebookSelectionInfo,
      toolSelect,
      toolButtons: {
        select: notebookToolSelect,
        lasso: notebookToolLasso,
        pen: notebookToolPen,
        eraser: notebookToolEraser,
      },
    },
    {
      onModeChange: (mode) => {
        applyToolPointerRouting(toolSelect.value);
        fitStageViewer();
      },
      onToolChange: (tool) => {
        toolSelect.value = tool;
        applyToolPointerRouting(tool);
        updateSelectionActionBar();
      },
    }
  );
}

/**
 * Routes the pointer to the mask canvas (convert-tab mask editing) or to the
 * ink canvas (vector editing). In the Defter workspace the eraser deletes
 * vector objects, so the ink canvas must receive the gesture; only the
 * Dönüştür workspace keeps the legacy mask-erase behavior.
 */
function applyToolPointerRouting(tool: string): void {
  const useMaskErase = tool === 'erase' && !isNotebookMode() && maskCanvas.width > 0;
  if (useMaskErase) {
    maskCanvas.style.display = 'block';
    maskCanvas.style.pointerEvents = 'auto';
    maskCanvas.style.zIndex = '20';
    inkSvg.style.pointerEvents = 'none';
  } else {
    maskCanvas.style.display = 'none';
    inkSvg.style.pointerEvents = 'auto';
  }
}

function isNotebookMode(): boolean {
  return notebookController?.getMode() === 'notebook';
}

// Wire notebook toolbar buttons
if (notebookLibraryBtn) {
  notebookLibraryBtn.addEventListener('click', () => {
    openLibraryBtn.click();
  });
}
if (notebookAddBlankBtn) {
  notebookAddBlankBtn.addEventListener('click', async () => {
    // TN-06: blank page requires no photo, model, or worker job. Online and
    // authenticated: server-backed page create (document + empty cim-1.1 head,
    // deduped by client-generated UUID). Otherwise: local-only blank page.
    notebookAddBlankBtn.disabled = true;
    try {
      const clientDocId = (crypto as any)?.randomUUID?.() || `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      let documentId = clientDocId;
      let headRevId: string | null = null;
      if (authLibrary.isAuthenticated()) {
        try {
          const notebook = await apiClient.notebooks.getDefault();
          const created = await apiClient.notebooks.createPage(
            notebook.id,
            { document_id: clientDocId, title: 'Boş sayfa', origin: 'blank', page_size: { width: 1600, height: 2263 } },
            clientDocId
          );
          documentId = created.page.document_id;
          headRevId = created.page.head_revision_id;
          statusDiv.textContent = `Yeni boş sayfa eklendi (sayfa ${created.page.position}).`;
        } catch (err: any) {
          statusDiv.textContent = `Sunucu sayfa oluşturma başarısız, yerel boş sayfa açılıyor: ${err.message}`;
        }
      } else {
        statusDiv.textContent = 'Yerel boş sayfa açıldı (oturum yok; kaydetmek için giriş yapın).';
      }
      const blankDoc = createBlankCIMDocument(documentId, headRevId || `local_rev_${Date.now()}`);
      branchWorkflow = new BranchWorkflowManager(blankDoc);
      authLibrary.setCurrentDocId(documentId);
      if (headRevId) authLibrary.setCurrentHeadRevisionId(headRevId);
      await syncUIFromDocument(blankDoc);
      initSyncManager(documentId, headRevId);
      updateSourceControls();
    } catch (err: any) {
      statusDiv.textContent = `Boş sayfa hatası: ${err.message}`;
    } finally {
      notebookAddBlankBtn.disabled = false;
    }
  });
}

// Marquee selection -> fresh blank canvas. The source canvas is preserved
// (copy semantics); the new page inherits the source provenance so object
// origins stay truthful (reconstructed content never lands on a "user" page).
async function transferSelectionToBlankPage(): Promise<void> {
  if (!branchWorkflow) {
    statusDiv.textContent = 'Aktarılacak bir tuval yok.';
    return;
  }
  const sourceDoc = branchWorkflow.getActiveDocument();
  const selectedIds = Array.from(state.selectedIds);
  if (selectedIds.length === 0) {
    statusDiv.textContent = 'Önce lasso ile bir alan seçin (mavi kutu).';
    return;
  }

  if (transferSelectionBtn) transferSelectionBtn.disabled = true;
  if (notebookTransferBtn) notebookTransferBtn.disabled = true;
  if (blankCanvasBtn) blankCanvasBtn.disabled = true;
  try {
    const clientDocId = (crypto as any)?.randomUUID?.() || `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    let documentId = clientDocId;
    let headRevId: string | null = null;
    const regionTitle = `${currentDocumentTitle || 'Belge'} — bölge`;

    if (authLibrary.isAuthenticated()) {
      try {
        const notebook = await apiClient.notebooks.getDefault();
        const created = await apiClient.notebooks.createPage(
          notebook.id,
          {
            document_id: clientDocId,
            title: regionTitle,
            origin: 'blank',
            page_size: { width: sourceDoc.page.width, height: sourceDoc.page.height },
          },
          clientDocId
        );
        documentId = created.page.document_id;
        headRevId = created.page.head_revision_id;
      } catch (err: any) {
        statusDiv.textContent = `Sunucu sayfa oluşturma başarısız, yerel boş sayfa açılıyor: ${err.message}`;
      }
    }

    const targetDoc = buildTransferredDocument(sourceDoc, selectedIds, {
      documentId,
      revisionId: headRevId || `local_rev_${Date.now()}`,
    });
    branchWorkflow = new BranchWorkflowManager(targetDoc);
    authLibrary.setCurrentDocId(documentId);
    if (headRevId) authLibrary.setCurrentHeadRevisionId(headRevId);
    currentDocumentTitle = regionTitle;
    await syncUIFromDocument(targetDoc);
    initSyncManager(documentId, headRevId);
    updateSourceControls();
    notebookController?.setMode('notebook');
    await autoSaveDraft();
    // "Geçir" completes here: the selection lands on the fresh canvas and the
    // context option retires (nothing stays selected to re-transfer by accident).
    clearSelection();
    statusDiv.textContent = `${selectedIds.length} nesne boş canvasa geçirildi.`;
  } catch (err: any) {
    statusDiv.textContent = `Aktarma hatası: ${err.message}`;
  } finally {
    if (transferSelectionBtn) transferSelectionBtn.disabled = false;
    if (notebookTransferBtn) notebookTransferBtn.disabled = false;
    if (blankCanvasBtn) blankCanvasBtn.disabled = false;
  }
}

transferSelectionBtn?.addEventListener('click', () => {
  void transferSelectionToBlankPage();
});
notebookTransferBtn?.addEventListener('click', () => {
  void transferSelectionToBlankPage();
});
blankCanvasBtn?.addEventListener('click', () => {
  void transferSelectionToBlankPage();
});
clearSelectionBtn?.addEventListener('click', () => {
  clearSelection();
});
document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Escape' && state.selectedIds.size > 0) {
    clearSelection();
  }
});
toolSelect.addEventListener('change', () => {
  const tool = toolSelect.value;
  notebookController?.setActiveTool(tool as any);
  applyToolPointerRouting(tool);
  updateSelectionActionBar();
});
if (notebookSaveBtn) {
  notebookSaveBtn.addEventListener('click', () => {
    saveCloudBtn.click();
  });
}
if (notebookExportBtn) {
  notebookExportBtn.addEventListener('click', () => {
    exportSvgBtn.click();
  });
}
if (notebookUndo) {
  notebookUndo.addEventListener('click', () => {
    undoBtn.click();
  });
}
if (notebookRedo) {
  notebookRedo.addEventListener('click', () => {
    redoBtn.click();
  });
}
if (notebookColor) {
  notebookColor.addEventListener('input', () => {
    colorInput.value = notebookColor.value;
  });
  colorInput.addEventListener('input', () => {
    if (notebookColor) notebookColor.value = colorInput.value;
  });
}
if (notebookWidth) {
  notebookWidth.addEventListener('input', () => {
    widthInput.value = notebookWidth.value;
  });
  widthInput.addEventListener('input', () => {
    if (notebookWidth) notebookWidth.value = widthInput.value;
  });
}

// Tablet Pointer Controller
pointerController = new TabletPointerController(inkSvg, {
  getDocument: () => (branchWorkflow ? branchWorkflow.getActiveDocument() : {
    version: '1.0',
    schema_version: '1.0.0',
    page: { width: state.width || 800, height: state.height || 600, dpi: 300 },
    source: { kind: 'empty', source_deleted: false },
    objects: state.objects || [],
    groups: [],
    layers: [],
    history: { undo_stack: [], redo_stack: [] },
    review: { method: 'sbb_threshold', candidate_labels: [], user_declaration: null },
  } as any),
  getTool: () => (toolSelect.value as any) || 'select',
  getSelectedIds: () => state.selectedIds,
  onSelect: (selectedIds, _primaryId) => {
    state.selectedIds = new Set(selectedIds);
    updateSelectionDisplay();
    applySelectionHighlight();
  },
  onCommitMove: (cmd) => {
    lastDragEndTime = Date.now();
    if (!branchWorkflow) return;
    const doc = branchWorkflow.applyCommand(cmd);
    // Keep the existing SVG nodes. A full sync re-created every path and
    // reloaded the source image before the next gesture could start.
    state.objects = [...doc.objects];
    for (const obj of doc.objects) {
      if (!state.selectedIds.has(obj.id)) continue;
      const elem = pathToElementMap.get(obj.id);
      if (!elem) continue;
      const [a, b, c, d, e, f] = obj.transform;
      elem.setAttribute('transform', `matrix(${a}, ${b}, ${c}, ${d}, ${e}, ${f})`);
    }
    statusDiv.textContent = `${state.selectedIds.size} nesne taşındı.`;
    notebookController?.updateDocumentHeader({ syncState: 'dirty' });
    scheduleDebouncedAutoSave(500);
  },
  onCommitDraw: (cmd) => {
    if (!branchWorkflow) {
      // First pen stroke on a page opened without a conversion/revision:
      // bootstrap a canonical blank cim-1.1 document so the stroke lands on
      // a valid, saveable CIM (TN-06). No model ran — user provenance.
      const documentId = authLibrary.getCurrentDocId() || `local_${Date.now()}`;
      const revisionId = `local_rev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      branchWorkflow = new BranchWorkflowManager(createBlankCIMDocument(documentId, revisionId));
      branchWorkflow.applyCommand(cmd);
      renderSVGStage(branchWorkflow.getActiveDocument());
      statusDiv.textContent = 'Boş sayfa oluşturuldu; kalem çizgisi eklendi.';
      scheduleDebouncedAutoSave(500);
      return;
    }

    branchWorkflow.applyCommand(cmd);
    const activeDoc = branchWorkflow.getActiveDocument();

    // Lightweight append for new stroke: zero rebuild of 830 existing SVG paths
    for (const obj of cmd.getAddedObjects()) {
      const pathElem = createPathElementForObject(obj, state.selectedIds.has(obj.id));
      pathToElementMap.set(obj.id, pathElem);
      inkSvg.appendChild(pathElem);
    }
    // The command already appended the stroke to the canonical document.
    // Never push into a state array that might share its backing array.
    state.objects = [...activeDoc.objects];

    objectCountDisplay.textContent = `Nesneler: ${activeDoc.objects.length}`;
    if (notebookController) {
      const activeBranch = branchWorkflow.getActiveBranch();
      notebookController.updateDocumentHeader({
        title: currentDocumentTitle,
        branchName: activeBranch?.name || state.activeBranch || 'main',
        syncState: 'dirty',
        selectedCount: state.selectedIds.size,
      });
    }
    statusDiv.textContent = 'Kalem çizgisi eklendi.';
    scheduleDebouncedAutoSave(500);
  },
  onLassoSelect: (selectedIds) => {
    state.selectedIds = new Set(selectedIds);
    updateSelectionDisplay();
    applySelectionHighlight();
    statusDiv.textContent = `Lasso ile ${selectedIds.length} nesne seçildi.`;
  },
  onCommitErase: (objectIds) => {
    if (!branchWorkflow || objectIds.length === 0) return;
    const cmd = new DeleteObjectsCommand(objectIds);
    const doc = branchWorkflow.applyCommand(cmd);
    state.selectedIds.clear();
    syncUIFromDocument(doc);
    statusDiv.textContent = `${objectIds.length} nesne silindi.`;
    notebookController?.updateDocumentHeader({ syncState: 'dirty' });
    scheduleDebouncedAutoSave(500);
  },
  onStatus: (msg) => {
    statusDiv.textContent = msg;
  },
  getStrokeColor: () => (notebookColor?.value || colorInput.value || '#163d58'),
  getStrokeWidth: () => (Number(notebookWidth?.value || widthInput.value) || 4),
});

window.addEventListener('resize', () => {
  fitStageViewer();
  fitSourceViewer();
});

function updateSourceControls(): void {
  if (!deleteSourceBtn) return;
  const docId = authLibrary ? authLibrary.getCurrentDocId() : null;
  const currentDoc = branchWorkflow ? branchWorkflow.getActiveDocument() : null;
  const enabled = canDeleteSource(
    docId,
    currentDoc,
    Boolean(state.source),
    state.width,
    state.height
  );
  deleteSourceBtn.disabled = !enabled;
  updateRegionControls();
}

// ---------------------------------------------------------------------------
// Region-first conversion (ADR-0006): draw a Windows-style box on the source
// photo, convert only that native-resolution crop, land the result on a fresh
// blank canvas. The whole page is never vectorized.
// ---------------------------------------------------------------------------

let sourceMarquee: SourceMarquee | null = null;
let regionSelection: Rect | null = null;
/** Model resolution contract: the server rejects inputs below this side. */
const MIN_CONVERSION_SIDE = 128;

function ensureSourceMarquee(): SourceMarquee | null {
  if (!sourceCanvas || !sourceCard) return null;
  if (!sourceMarquee) {
    sourceMarquee = new SourceMarquee(sourceCanvas, sourceCard, {
      onSelectionChange: (rect) => {
        inkMaskReview?.resetSource();
        regionSelection = rect;
        updateRegionControls();
        if (rect) {
          statusDiv.textContent = `Bölge seçildi: ${rect.width} × ${rect.height} px (kaynak çözünürlük).`;
        }
      },
    });
  }
  return sourceMarquee;
}

function updateRegionControls(): void {
  const hasSource = Boolean(state.source) && state.width > 0 && state.height > 0;
  if (sourceRegionBtn) sourceRegionBtn.disabled = !hasSource;
  if (sourceProposeBtn) sourceProposeBtn.disabled = !hasSource;
  if (inkPreviewBtn) inkPreviewBtn.disabled = !hasSource;
  if (sourceRegionConvertBtn) {
    sourceRegionConvertBtn.disabled = !hasSource || !regionSelection || regionSelection.width < 8 || regionSelection.height < 8;
  }
}

/**
 * "Bölge Öner": propose the densest content region(s) with a classical,
 * model-free pipeline (Otsu -> components -> tight bboxes), then hand the
 * best one to the editable marquee so the USER refines it before conversion.
 * Analysis runs on a downsampled copy; the box is scaled back to native px.
 */
async function proposeRegionFromSource(): Promise<void> {
  if (!state.source || state.width <= 0 || state.height <= 0) return;
  statusDiv.textContent = 'Bölge önerisi hesaplanıyor...';
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Kaynak görsel çözümlenemedi.'));
      img.src = state.source as string;
    });

    // Downsampled analysis canvas: tablet photos stay millisecond-fast.
    const maxSide = 256;
    const scale = Math.min(1, maxSide / Math.max(state.width, state.height));
    const w = Math.max(1, Math.round(state.width * scale));
    const h = Math.max(1, Math.round(state.height * scale));
    const analysis = document.createElement('canvas');
    analysis.width = w;
    analysis.height = h;
    const ctx = analysis.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Analiz tuvali oluşturulamadı.');
    ctx.drawImage(image, 0, 0, w, h);
    const frame = ctx.getImageData(0, 0, w, h);
    const luminance = luminanceFromImageData(frame.data, w, h);

    const regions = proposeRegionsFromLuminance(luminance, w, h, { stride: 1, mergeRadius: 1, maxRegions: 6 });
    if (regions.length === 0) {
      statusDiv.textContent = 'Belirgin bir içerik bölgesi bulunamadı; kutuyu elle çizebilirsiniz.';
      return;
    }
    const best = regions[0];
    const nativeRect = scaleRectToSource(best, w, h, state.width, state.height);
    // The server rejects inputs below 128 px (model resolution contract); a
    // small proposal must still be convertible, so grow the box on its center.
    const expanded = expandRectToMinSide(nativeRect, MIN_CONVERSION_SIDE, state.width, state.height);

    const marquee = ensureSourceMarquee();
    if (!marquee) return;
    marquee.setSourceSize(state.width, state.height);
    marquee.setSelection(expanded);
    regionSelection = expanded;
    updateRegionControls();
    statusDiv.textContent = `Bölge önerildi (${expanded.width} × ${expanded.height} px, ${regions.length} aday). Kutuyu elle düzeltebilirsiniz.`;
  } catch (err: any) {
    statusDiv.textContent = `Bölge önerisi hatası: ${err.message}`;
  }
}

if (sourceProposeBtn) {
  sourceProposeBtn.addEventListener('click', () => {
    void proposeRegionFromSource();
  });
}

// ---------------------------------------------------------------------------
// Native-pixel mask review (VS-SLICE-10), computed locally in a worker.
// Existing conversion still receives the original region, never a preview.
// ---------------------------------------------------------------------------
const inkMaskReview = inkPreviewCanvas
  ? new InkMaskReview(sourceCanvas, inkPreviewCanvas, text => { statusDiv.textContent = text; }, () => regionSelection)
  : null;
inkPreviewBtn?.addEventListener('click', () => {
  if (state.source) inkMaskReview?.toggleVisible();
});

if (sourceRegionBtn) {
  sourceRegionBtn.addEventListener('click', () => {
    inkMaskReview?.hide();
    const marquee = ensureSourceMarquee();
    if (!marquee) return;
    marquee.setSourceSize(state.width, state.height);
    marquee.clear();
    regionSelection = null;
    updateRegionControls();
    statusDiv.textContent = 'Fotoğraf üzerinde bir bölge çerçeveleyin; kutuyu kenarlarından çekip yeniden boyutlandırabilir, içinden sürükleyip taşıyabilirsiniz.';
  });
}

// Escape releases the current region box (does not touch editor selection).
document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Escape' && sourceMarquee && sourceMarquee.getSelection()) {
    sourceMarquee.clear();
    regionSelection = null;
    updateRegionControls();
    statusDiv.textContent = 'Bölge seçimi temizlendi.';
  }
});

/**
 * Convert only the selected region: crop at native resolution, upload it as
 * the input asset, create a fresh page, submit the conversion, and let the
 * existing job pipeline apply the result. The page never receives the whole
 * photo, so conversion cost scales with the region, not the page.
 */
async function convertSelectedRegion(): Promise<void> {
  if (!regionSelection) {
    statusDiv.textContent = 'Önce fotoğraf üzerinde bir bölge seçin.';
    return;
  }
  if (!state.source) {
    statusDiv.textContent = 'Kaynak görsel yok.';
    return;
  }

  if (sourceRegionConvertBtn) sourceRegionConvertBtn.disabled = true;
  setBusy(true);

  // Server-side resolution contract: grow an under-sized box on its center so
  // a small hand-drawn selection still converts instead of failing upstream.
  const conversionRect = expandRectToMinSide(
    regionSelection,
    MIN_CONVERSION_SIDE,
    state.width,
    state.height
  );
  if (conversionRect.width !== regionSelection.width || conversionRect.height !== regionSelection.height) {
    statusDiv.textContent = `Bölge en az ${MIN_CONVERSION_SIDE} px olacak şekilde genişletildi (${conversionRect.width} × ${conversionRect.height} px).`;
  } else {
    statusDiv.textContent = `Seçilen bölge dönüştürülüyor (${conversionRect.width} × ${conversionRect.height} px)...`;
  }

  try {
    const cropBlob = await cropDataUrlToBlob(state.source, conversionRect);
    const stamp = Date.now();

    if (!authLibrary.isAuthenticated()) {
      // Local-only fallback: no worker without a session. The crop is rendered
      // on the source panel so the user still sees exactly what was chosen.
      statusDiv.textContent = 'Bölge dönüşümü için giriş yapın (yerel model tuvale işlenemiyor).';
      return;
    }

    const documentId = (crypto as any)?.randomUUID?.() || `local_${stamp}`;
    // 1. Upload the native-resolution crop as the conversion input.
    const file = new File([cropBlob], `region_${documentId.substring(0, 8)}.png`, { type: 'image/png' });
    const uploadRes = await authLibrary.uploadPhoto(file, branchWorkflow ? branchWorkflow.getActiveDocument() : undefined);
    const assetId = uploadRes.complete.asset_id;

    // 2. Fresh document for the region. The notebook-pages API pins its canvas
    //    to 1600x2263; a region result must keep the crop's own dimensions, so
    //    this uses the plain document path (same as the Dönüştür flow): the
    //    worker builds the result CIM with the crop's page size.
    const created = await authLibrary.createDocument(`${currentDocumentTitle || 'Belge'} — bölge`);
    const pageDocId = created.id;
    const headRevId = created.head_revision_id;

    const blankDoc = createBlankCIMDocument(pageDocId, headRevId || `local_rev_${stamp}`);
    blankDoc.page = { ...blankDoc.page, width: conversionRect.width, height: conversionRect.height };
    // The blank page keeps user provenance with null source assets (the
    // provenance contract); the crop is submitted explicitly as the conversion
    // input, and the worker writes the model provenance + asset id into the
    // result revision. Pre-setting an asset id here would contradict the
    // user-provenance rule the client validator enforces.
    branchWorkflow = new BranchWorkflowManager(blankDoc);
    authLibrary.setCurrentDocId(pageDocId);
    if (headRevId) authLibrary.setCurrentHeadRevisionId(headRevId);

    // 3. Submit the conversion; the existing job UI applies the result to this
    //    new document when it completes (region is the only thing it ever saw).
    await syncUIFromDocument(blankDoc);
    initSyncManager(pageDocId, headRevId);
    currentDocumentTitle = `${currentDocumentTitle || 'Belge'} — bölge`;

    const pending = await jobManager.submitConversion(pageDocId, assetId, {
      modelVersion: methodSelect.value,
    });
    jobUI.show(pending);
    notebookController?.setMode('notebook');
    updateRegionControls();
    statusDiv.textContent = 'Bölge için dönüştürme işi başlatıldı; sonuç bu yeni boş sayfaya uygulanacak.';
  } catch (err: any) {
    statusDiv.textContent = `Bölge dönüştürme hatası: ${err.message}`;
  } finally {
    setBusy(false);
    updateRegionControls();
  }
}

sourceRegionConvertBtn?.addEventListener('click', () => {
  void convertSelectedRegion();
});

// Helper: sync UI with document
async function syncUIFromDocument(doc: CIMDocument): Promise<void> {
  state.objects = doc.objects.map((obj: any) => {
    if (obj.transform) {
      if (obj.x === undefined) obj.x = obj.transform[4] || 0;
      if (obj.y === undefined) obj.y = obj.transform[5] || 0;
      if (obj.scale === undefined) obj.scale = obj.transform[0] || 1;
    }
    return obj;
  });
  objectCountDisplay.textContent = `Nesneler: ${doc.objects.length}`;

  if (branchWorkflow) {
    const active = branchWorkflow.getActiveBranch();
    state.activeBranch = active.name;
    branchInfo.textContent = `Branch: ${active.name}`;

    // Update branch select options
    const branches = branchWorkflow.listBranches();
    state.branches = branches.map((b) => b.name);
    branchSelect.innerHTML = '';
    for (const b of branches) {
      const opt = document.createElement('option');
      opt.value = b.id;
      opt.textContent = `Dal: ${b.name}`;
      if (b.id === active.id) opt.selected = true;
      branchSelect.appendChild(opt);
    }
  }

  // Update review state
  const decl = doc.review.user_declaration;
  state.review.userReview = {
    confirmed: Boolean(decl?.confirmed),
    hash: decl?.content_hash,
    time: decl?.declaration_time,
  };

  if (decl?.confirmed) {
    reviewDiv.textContent = `İnceleme durumu: Kullanıcı tarafından doğrulandı (${decl.content_hash?.slice(0, 8)}...)`;
  } else {
    reviewDiv.textContent = 'İnceleme durumu: İnceleme bekliyor (onaylanmadı)';
  }

  renderSVGStage(doc);
  await restoreSourceForDocument(doc);
  updateSourceControls();

  if (notebookController) {
    const activeBranch = branchWorkflow ? branchWorkflow.getActiveBranch() : null;
    const isDirty = branchWorkflow ? branchWorkflow.hasManualEdits() : false;
    let syncState: 'clean' | 'dirty' | 'saving' | 'conflict' = isDirty ? 'dirty' : 'clean';
    if (syncManager) {
      const s = syncManager.getSyncState();
      if (s === 'conflict') syncState = 'conflict';
      else if (s === 'saving') syncState = 'saving';
      else if (s === 'dirty') syncState = 'dirty';
      else if (s === 'clean') syncState = 'clean';
    }
    notebookController.updateDocumentHeader({
      title: currentDocumentTitle,
      branchName: activeBranch?.name || state.activeBranch || 'main',
      syncState,
      selectedCount: state.selectedIds.size,
    });
  }
}

function renderSource(dataUrl: string, assetId: string | null = null): Promise<void> {
  inkMaskReview?.resetSource();
  sourceMarquee?.clear();
  regionSelection = null;
  state.source = dataUrl;
  state.sourceAssetId = assetId;
  if (assetId) sourceByAssetId.set(assetId, dataUrl);
  return new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => {
      inkMaskReview?.resetSource();
      sourceMarquee?.clear();
      regionSelection = null;
      state.width = img.naturalWidth;
      state.height = img.naturalHeight;
      sourceCanvas.width = state.width;
      sourceCanvas.height = state.height;
      const ctx = sourceCanvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      sourcePreview.src = dataUrl;
      sourceDimensions.textContent = `${state.width} × ${state.height} px`;
      fitSourceViewer();
      ensureSourceMarquee()?.setSourceSize(state.width, state.height);
      updateSourceControls();
      resolve();
    };
    img.onerror = () => {
      updateSourceControls();
      resolve();
    };
    img.src = dataUrl;
  });
}

function clearRenderedSource(): void {
  inkMaskReview?.resetSource();
  sourceMarquee?.clear();
  regionSelection = null;
  state.source = null;
  state.sourceAssetId = null;
  state.width = 0;
  state.height = 0;
  sourceCanvas.width = 0;
  sourceCanvas.height = 0;
  sourcePreview.removeAttribute('src');
  sourceDimensions.textContent = 'Kaynak yok';
  updateSourceControls();
}

async function restoreSourceForDocument(doc: CIMDocument): Promise<void> {
  let assetId = doc.source.original_asset_id || doc.source.prepared_asset_id || doc.source.normalized_asset_id || null;
  if (!assetId && authLibrary && authLibrary.getCurrentDocId()) {
    try {
      const docRes = await apiClient.documents.get(authLibrary.getCurrentDocId()!);
      if (docRes.source_id && !doc.source.source_deleted) {
        assetId = docRes.source_id;
        doc.source.original_asset_id = assetId;
      }
    } catch (_) {}
  }

  if (!assetId || doc.source.source_deleted) {
    clearRenderedSource();
    return;
  }

  const cached = sourceByAssetId.get(assetId);
  if (cached) {
    await renderSource(cached, assetId);
    return;
  }

  try {
    const blob = await apiClient.assets.getContent(assetId);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error || new Error('Source read failed'));
      reader.readAsDataURL(blob);
    });
    await renderSource(dataUrl, assetId);
  } catch (err) {
    clearRenderedSource();
    statusDiv.textContent = 'Kaynak görsel kullanılamıyor veya silinmiş.';
  }
}

function fitSourceViewer(): void {
  const viewport = sourceCanvas.parentElement;
  if (!viewport || state.width <= 0 || state.height <= 0) return;
  const fit = fitContent(state.width, state.height, viewport.clientWidth, viewport.clientHeight);
  sourceCanvas.style.width = `${fit.width}px`;
  sourceCanvas.style.height = `${fit.height}px`;
  inkMaskReview?.align();
}

function fitStageViewer(): void {
  if (!branchWorkflow) return;
  const viewport = stage.parentElement?.parentElement;
  const doc = branchWorkflow.getActiveDocument();
  if (!viewport) return;
  const fit = fitContent(doc.page.width, doc.page.height, viewport.clientWidth, viewport.clientHeight);
  const viewportWidth = Math.max(1, Math.round(fit.width));
  const viewportHeight = Math.max(1, Math.round(fit.height));
  const contentWidth = Math.max(1, Math.round(fit.width * stageZoom));
  const contentHeight = Math.max(1, Math.round(fit.height * stageZoom));
  const scrollMargin = 16;
  // The wrapper is the camera viewport; the stage is the zoomed scroll content.
  stageWrapper.style.width = `${viewportWidth}px`;
  stageWrapper.style.height = `${viewportHeight}px`;
  stage.style.width = `${contentWidth + scrollMargin}px`;
  stage.style.height = `${contentHeight + scrollMargin}px`;
  inkSvg.style.width = `${contentWidth}px`;
  inkSvg.style.height = `${contentHeight}px`;
}

function createPathElementForObject(obj: InkObject, isSelected: boolean): SVGPathElement {
  const pathElem = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  pathElem.setAttribute('d', renderObjectToSVGPath(obj));
  pathElem.setAttribute('data-id', obj.id);

  const [a, b, c, d, e, f] = obj.transform;
  if (a !== 1 || b !== 0 || c !== 0 || d !== 1 || e !== 0 || f !== 0) {
    pathElem.setAttribute('transform', `matrix(${a}, ${b}, ${c}, ${d}, ${e}, ${f})`);
  }

  if (obj.kind === 'compound_fill') {
    pathElem.setAttribute('fill', obj.color);
    pathElem.setAttribute('fill-rule', obj.fill_rule);
    pathElem.setAttribute('opacity', String(obj.opacity));
    if (isSelected) {
      pathElem.classList.add('selected');
    }
  } else if (obj.kind === 'centerline_stroke') {
    pathElem.setAttribute('fill', 'none');
    pathElem.setAttribute('stroke', obj.color);
    pathElem.setAttribute('stroke-width', String(obj.width));
    pathElem.setAttribute('stroke-linecap', 'round');
    pathElem.setAttribute('stroke-linejoin', 'round');
    pathElem.setAttribute('opacity', String(obj.opacity));
    if (isSelected) {
      pathElem.classList.add('selected');
    }
  }

  return pathElem;
}

// Render pure vector SVG stage
function renderSVGStage(doc: CIMDocument) {
  inkSvg.setAttribute('viewBox', `0 0 ${doc.page.width} ${doc.page.height}`);
  fitStageViewer();

  // Strict invariant: Clear all child nodes. Zero <image> tags, zero raster base64!
  while (inkSvg.firstChild) {
    inkSvg.removeChild(inkSvg.firstChild);
  }
  pathToElementMap.clear();

  for (const obj of doc.objects) {
    const pathElem = createPathElementForObject(obj, state.selectedIds.has(obj.id));
    pathToElementMap.set(obj.id, pathElem);
    inkSvg.appendChild(pathElem);
  }

  // Update selection display
  updateSelectionDisplay();
}

function applySelectionHighlight(): void {
  // Highlighting tens of thousands of SVG paths with a drop-shadow filter made
  // a large marquee selection stall the page. Above the cap we skip per-element
  // highlighting entirely: the selection is still real (status bar + context
  // bar show the count) and every operation still works on it.
  const HIGHLIGHT_CAP = 2000;
  const highlightEnabled = state.selectedIds.size <= HIGHLIGHT_CAP;
  for (const [id, elem] of pathToElementMap) {
    if (highlightEnabled && state.selectedIds.has(id)) {
      elem.classList.add('selected');
    } else {
      elem.classList.remove('selected');
    }
  }
}
function updateSelectionDisplay(): void {
  const text = state.selectedIds.size > 0 ? `Seçim: ${state.selectedIds.size} nesne` : 'Seçim: Yok';
  if (selectionInfo) selectionInfo.textContent = text;
  if (notebookSelectionInfo) notebookSelectionInfo.textContent = text;
  if (notebookController) {
    notebookController.updateDocumentHeader({
      selectedCount: state.selectedIds.size,
    });
  }
  updateSelectionActionBar();
}

// ---------------------------------------------------------------------------
// Floating selection action bar ("Boş canvasa geçir" option)
//
// Design: after a marquee (or click) selection the bar is anchored to the
// selection's screen-space bounding box (top-right, flipping below when there
// is no room above). It offers exactly one transfer action plus "Temizle";
// Escape clears the selection too. The bar hides whenever the selection is
// empty or the active tool is not a selection tool.
// ---------------------------------------------------------------------------

function updateSelectionActionBar(): void {
  const bar = selectionActionBar;
  if (!bar) return;
  const visible = shouldShowBlankCanvasOption(
    state.selectedIds.size,
    toolSelect.value,
    Boolean(branchWorkflow)
  );
  if (!visible) {
    bar.style.display = 'none';
    return;
  }
  if (selectionCountLabel) {
    selectionCountLabel.textContent = `${state.selectedIds.size} nesne seçildi`;
  }
  bar.style.display = 'flex';
  positionSelectionActionBar();
}

function positionSelectionActionBar(): void {
  const bar = selectionActionBar;
  if (!bar || !branchWorkflow) return;
  const container = bar.parentElement;
  if (!container) return;

  // Selection bounding box in document space.
  const doc = branchWorkflow.getActiveDocument();
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const obj of doc.objects) {
    if (!state.selectedIds.has(obj.id)) continue;
    const b = computeObjectBounds(obj);
    if (b[0] < minX) minX = b[0];
    if (b[1] < minY) minY = b[1];
    if (b[2] > maxX) maxX = b[2];
    if (b[3] > maxY) maxY = b[3];
  }

  const cRect = container.getBoundingClientRect();
  let anchorX = cRect.width / 2;
  let anchorY = 10;
  const ctm = typeof inkSvg.getScreenCTM === 'function' ? inkSvg.getScreenCTM() : null;
  if (ctm && Number.isFinite(minX) && Number.isFinite(maxX)) {
    const toClient = (x: number, y: number) => ({
      x: ctm.a * x + ctm.c * y + ctm.e,
      y: ctm.b * x + ctm.d * y + ctm.f,
    });
    const a = toClient(minX, minY);
    const b = toClient(maxX, maxY);
    anchorX = Math.max(a.x, b.x) - cRect.left;
    anchorY = Math.min(a.y, b.y) - cRect.top;
  }

  const barW = bar.offsetWidth || 240;
  const barH = bar.offsetHeight || 32;
  let left = anchorX - barW;
  let top = anchorY - barH - 8;
  if (top < 8) top = anchorY + 8; // no room above: flip below the selection
  left = Math.min(Math.max(8, left), Math.max(8, cRect.width - barW - 8));
  top = Math.min(Math.max(8, top), Math.max(8, cRect.height - barH - 8));
  bar.style.left = `${left}px`;
  bar.style.top = `${top}px`;
}

function clearSelection(): void {
  state.selectedIds.clear();
  updateSelectionDisplay();
  applySelectionHighlight();
}

// Selection handler
function handleSelectObject(id: string, multi: boolean) {
  if (multi) {
    if (state.selectedIds.has(id)) {
      state.selectedIds.delete(id);
    } else {
      state.selectedIds.add(id);
    }
  } else {
    state.selectedIds.clear();
    state.selectedIds.add(id);
  }
  updateSelectionDisplay();
  applySelectionHighlight();
}

// Delegated stage click for object selection and canvas deselection
inkSvg.addEventListener('click', (ev) => {
  if (Date.now() - lastDragEndTime < 300) return;
  const target = ev.target as SVGElement | null;
  if (!target) return;

  const pathElem = (typeof target.closest === 'function' ? target.closest('[data-id]') : null) as SVGElement | null;
  const objId = pathElem?.getAttribute('data-id') || target.getAttribute('data-id');

  if (objId) {
    ev.stopPropagation();
    handleSelectObject(objId, ev.shiftKey);
    return;
  }

  if (toolSelect.value === 'select' && target === inkSvg) {
    state.selectedIds.clear();
    updateSelectionDisplay();
    applySelectionHighlight();
  }
});

// Auto-save draft helper
async function autoSaveDraft() {
  if (autoSaveDebounceTimer !== null) {
    clearTimeout(autoSaveDebounceTimer);
    autoSaveDebounceTimer = null;
  }
  if (!branchWorkflow) return;
  try {
    const doc = branchWorkflow.getActiveDocument();
    await draftStore.saveDraft(doc, {
      isDirty: branchWorkflow.hasManualEdits(),
      branchId: branchWorkflow.getActiveBranch().id,
    });
    if (syncManager) {
      await syncManager.markDirty();
    }
    if (notebookController) {
      notebookController.updateDocumentHeader({
        syncState: 'dirty',
      });
    }
  } catch (err) {
    console.warn('Autosave draft failed:', err);
  }
}

// 1. Ingestion via CaptureManager
photoInput.addEventListener('change', async () => {
  const file = photoInput.files?.[0];
  if (!file) return;

  setBusy(true);
  statusDiv.textContent = 'Görsel yükleniyor ve EXIF doğrulanıyor...';

  try {
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

    const admission = captureManager.admitFile({
      name: file.name,
      size: file.size,
      type: file.type,
    });
    if (!admission.valid) {
      throw new Error(admission.error || 'Dosya kabul edilmedi.');
    }

    captureManager.initSession({
      name: file.name,
      size: file.size,
      type: file.type,
    });

    // Ingestion to cloud via actual Section 7 Uploads API if authenticated (SAAS-312)
    if (authLibrary.isAuthenticated()) {
      try {
        const uploadRes = await authLibrary.uploadPhoto(file, branchWorkflow ? branchWorkflow.getActiveDocument() : undefined);
        (state as any).lastUploadId = uploadRes.upload.id;
        (state as any).lastAssetId = uploadRes.complete.asset_id;
        if ((uploadRes.complete as any).document_id) {
          authLibrary.setCurrentDocId((uploadRes.complete as any).document_id);
        }
      } catch (uploadErr: any) {
        if (uploadErr instanceof ApiClientError && uploadErr.status === 401) {
          statusDiv.textContent = 'Oturum süresi doldu. Taslağınız yerel hafızada saklandı.';
        } else {
          console.warn('Backend upload pipeline warning:', uploadErr);
        }
      }
    }

    const assetId = (state as any).lastAssetId || null;
    renderSource(dataUrl, assetId);

    const img = new Image();
    img.onload = () => {
      inkMaskReview?.resetSource();
      sourceMarquee?.clear();
      regionSelection = null;
      state.width = img.naturalWidth;
      state.height = img.naturalHeight;

      captureManager.applyExifNormalization({
        rawWidth: state.width,
        rawHeight: state.height,
        orientation: 1,
      });

      sourceCanvas.width = state.width;
      sourceCanvas.height = state.height;
      const ctx = sourceCanvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      sourceDimensions.textContent = `${state.width} × ${state.height} px`;
      fitSourceViewer();
      statusDiv.textContent = `Görsel yüklendi: ${state.width} × ${state.height} px (${file.name})`;
      setBusy(false);
    };
    img.src = dataUrl;
  } catch (err: any) {
    statusDiv.textContent = `Yükleme hatası: ${err.message}`;
    setBusy(false);
  }
});

// 2. Quality Diagnostics
qualityBtn.addEventListener('click', async () => {
  if (!state.source) {
    qualityStatus.textContent = 'Lütfen önce bir görsel yükleyin.';
    return;
  }
  setBusy(true);
  try {
    let report: any;
    try {
      const resp = await fetch('/api/quality', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: state.source }),
      });
      if (resp.ok) {
        report = await resp.json();
      }
    } catch (_) {}

    if (!report) {
      report = {
        status: 'review_required',
        calibrated: false,
        metrics: { blur: 0.82, contrast: 0.76 },
        reasons: ['Uncalibrated capture diagnostics (heuristic only)'],
      };
    }

    report.calibrated = false;
    state.inputQuality = report;
    captureManager.setQualityAssessment({
      status: report.status === 'clean' ? 'clean' : 'review_required',
      calibrated: false,
      metrics: report.metrics || {},
      reasons: report.reasons || [],
      isTechnicalLimit: false,
    });

    qualityStatus.textContent = `Kalite: ${report.status} (Kalibrasyonsuz: true)`;
    statusDiv.textContent = 'Çekim kalitesi analiz edildi.';
  } catch (err: any) {
    qualityStatus.textContent = `Kalite analizi hatası: ${err.message}`;
  } finally {
    setBusy(false);
  }
});

// 3. Model Conversion
convertBtn.addEventListener('click', async () => {
  if (!state.source) {
    statusDiv.textContent = 'Lütfen önce bir görsel yükleyin.';
    return;
  }

  // Cloud SaaS background worker pipeline (§8, SAAS-410)
  if (authLibrary.isAuthenticated() && (state as any).lastAssetId) {
    setBusy(true);
    try {
      let docId = authLibrary.getCurrentDocId();
      if (!docId) {
        const newDoc = await authLibrary.createDocument(
          `Çizim ${new Date().toLocaleDateString('tr-TR')} ${new Date().toLocaleTimeString('tr-TR')}`
        );
        docId = newDoc.id;
      }

      statusDiv.textContent = 'Bulut dönüştürme işi kuyruğa alınıyor...';
      const pending = await jobManager.submitConversion(docId, (state as any).lastAssetId, {
        modelVersion: methodSelect.value,
        overrideFlag: qualityOverride.checked,
      });
      jobUI.show(pending);
      statusDiv.textContent = 'Dönüştürme işi başlatıldı.';
      return;
    } catch (jobErr: any) {
      console.warn('Background conversion submission error, falling back to local /api/process:', jobErr);
      statusDiv.textContent = `Bulut dönüştürme başlatılamadı: ${jobErr.message}. Yerel işleme deneniyor...`;
    } finally {
      setBusy(false);
    }
  }

  setBusy(true);
  statusDiv.textContent = 'Alpha mürekkep modeli çalıştırılıyor (/api/process)...';

  try {
    const method = methodSelect.value;
    const response = await fetch('/api/process', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image: state.source,
        method,
      }),
    });

    if (!response.ok) {
      const errJson = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errJson.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    const geo = data.geometry;

    // Convert server response geometries to CompoundFill objects
    const objects: CompoundFill[] = (geo.geometries || []).map((g: any, idx: number) => ({
      id: g.geometry_id || `geo_${idx + 1}`,
      kind: 'compound_fill' as const,
      rings: g.rings.map((r: any) => ({
        points: r.points as [number, number][],
      })),
      fill_rule: (geo.fill_rule || 'evenodd') as 'evenodd' | 'nonzero',
      color: colorInput.value || '#163d58',
      opacity: 1.0,
      transform: [1, 0, 0, 1, 0, 0] as [number, number, number, number, number, number],
      group_id: null,
      source_kind: 'reconstructed' as const,
    }));

    const cimDoc: CIMDocument = {
      schema_version: 'cim-1.0',
      document_id: `doc_${Date.now()}`,
      revision_id: `rev_1_${Date.now()}`,
      parent_revision_id: null,
      page: {
        width: state.width || geo.coordinate_frame.width,
        height: state.height || geo.coordinate_frame.height,
        unit: 'px',
        space: 'processed-image',
      },
      source: {
        original_to_processed: [1, 0, 0, 0, 1, 0, 0, 0, 1],
        source_deleted: false,
      },
      objects,
      provenance: {
        conversion_id: `conv_${Date.now()}`,
        model_version: method,
        model_manifest_hash: '0123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef',
        preprocess_version: '1.0.0',
        vectorizer_version: 'exact_compound_v1',
      },
      review: {
        requirement: 'human_review_recommended',
        assessment_kind: 'heuristic_uncalibrated',
        user_declaration: null,
      },
    };

    validateCIMDocument(cimDoc);
    branchWorkflow = new BranchWorkflowManager(cimDoc);

    // Setup mask canvas
    maskCanvas.width = cimDoc.page.width;
    maskCanvas.height = cimDoc.page.height;
    state.automatic = 'initial';
    state.corrected = 'initial';

    if (data.mask) {
      const maskImg = new Image();
      maskImg.onload = () => {
        const mctx = maskCanvas.getContext('2d');
        mctx?.drawImage(maskImg, 0, 0);
      };
      maskImg.src = data.mask;
    }

    state.review.method = method;
    currentDocumentTitle = 'Dönüştürülen Çizim';
    syncUIFromDocument(cimDoc);
    statusDiv.textContent = `Dönüştürme tamamlandı: ${objects.length} nesne oluşturuldu.`;

    await autoSaveDraft();
  } catch (err: any) {
    statusDiv.textContent = `Dönüştürme hatası: ${err.message}`;
  } finally {
    setBusy(false);
  }
});

// 4. Review Declaration
reviewConfirmBtn.addEventListener('click', async () => {
  if (!branchWorkflow) {
    alert('Önce bir belge dönüştürün veya açın.');
    return;
  }
  if (!checkDots.checked || !checkSigns.checked || !checkLabels.checked) {
    alert('Tüm kontrol onay kutularını işaretlemelisiniz.');
    return;
  }

  try {
    const doc = branchWorkflow.getActiveDocument();
    const decl = await createUserDeclaration(doc);
    branchWorkflow.setActiveDocumentDeclaration(decl);
    syncUIFromDocument(branchWorkflow.getActiveDocument());
    await autoSaveDraft();
    statusDiv.textContent = 'İnceleme onayı kaydedildi.';
  } catch (err: any) {
    alert(`İnceleme onaylama hatası: ${err.message}`);
  }
});

// 5. Editor Commands & Drag Interaction
// Object dragging, lasso selection, and stylus drawing are handled by TabletPointerController (Gate NT-1).

// Recolor
recolorBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  if (state.selectedIds.size === 0 && branchWorkflow.getActiveDocument().objects.length > 0) {
    state.selectedIds.add(branchWorkflow.getActiveDocument().objects[0].id);
  }
  if (state.selectedIds.size === 0) return;
  const newColor = colorInput.value || '#163d58';
  const cmd = new RecolorObjectsCommand(Array.from(state.selectedIds), newColor);
  branchWorkflow.applyCommand(cmd);
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = `${state.selectedIds.size} nesne ${newColor} rengine dönüştürüldü.`;
});

// Scale
scaleBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  if (state.selectedIds.size === 0 && branchWorkflow.getActiveDocument().objects.length > 0) {
    state.selectedIds.add(branchWorkflow.getActiveDocument().objects[0].id);
  }
  if (state.selectedIds.size === 0) return;
  const factor = 1.1;
  const cmd = new ScaleObjectsCommand(Array.from(state.selectedIds), factor);
  branchWorkflow.applyCommand(cmd);
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = `${state.selectedIds.size} nesne %110 ölçeklendirildi.`;
});

// Rotate (VS-SLICE-1: transform-only rotation around the selection bbox center)
rotateBtn?.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  const doc = branchWorkflow.getActiveDocument();
  const targets = state.selectedIds.size > 0
    ? Array.from(state.selectedIds)
    : doc.objects.length > 0 ? [doc.objects[0].id] : [];
  if (targets.length === 0) return;
  const bounds = doc.objects
    .filter((o) => targets.includes(o.id))
    .map((o) => computeObjectBounds(o));
  if (bounds.length === 0) return;
  const minX = Math.min(...bounds.map((b) => b[0]));
  const minY = Math.min(...bounds.map((b) => b[1]));
  const maxX = Math.max(...bounds.map((b) => b[2]));
  const maxY = Math.max(...bounds.map((b) => b[3]));
  const pivot: [number, number] = [(minX + maxX) / 2, (minY + maxY) / 2];
  const cmd = new RotateObjectsCommand(targets, 15, pivot);
  branchWorkflow.applyCommand(cmd);
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = `${targets.length} nesne seçim merkezi etrafında 15 derece döndürüldü.`;
});

// SVG import (VS-SLICE-1: interchange only; never mounted as live DOM)
async function importSvgFile(file: File): Promise<void> {
  if (!branchWorkflow) return;
  try {
    const text = await file.text();
    const doc = branchWorkflow.getActiveDocument();
    const result = importSVG(text, doc);
    if (result.objects.length === 0) {
      statusDiv.textContent = `SVG içe aktarılamadı: geçerli şekil yok (${result.skipped.length} eleman atlandı).`;
      return;
    }
    const cmd = new AddObjectsCommand(result.objects);
    branchWorkflow.applyCommand(cmd);
    syncUIFromDocument(branchWorkflow.getActiveDocument());
    await autoSaveDraft();
    const skipNote = result.skipped.length > 0 ? `, ${result.skipped.length} eleman atlandı` : '';
    statusDiv.textContent = `SVG içe aktarıldı: ${result.objects.length} nesne eklendi${skipNote}.`;
  } catch (err) {
    statusDiv.textContent = `SVG içe aktarma hatası: ${err instanceof Error ? err.message : String(err)}`;
  }
}

svgImportInput?.addEventListener('change', async () => {
  const file = svgImportInput.files?.[0];
  svgImportInput.value = '';
  if (file) await importSvgFile(file);
});

// Group / Ungroup
groupBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  if (state.selectedIds.size === 0 && branchWorkflow.getActiveDocument().objects.length > 0) {
    state.selectedIds.add(branchWorkflow.getActiveDocument().objects[0].id);
  }
  if (state.selectedIds.size === 0) return;
  const doc = branchWorkflow.getActiveDocument();
  const gid = `group_${Date.now()}`;
  const updated = doc.objects.map((o) =>
    state.selectedIds.has(o.id) ? { ...o, group_id: gid, group: gid } : o
  );
  branchWorkflow.applyCommand({
    description: `Group ${state.selectedIds.size} objects`,
    execute: (d) => ({ ...d, objects: updated, review: { ...d.review, user_declaration: null } }),
    undo: (d) => ({ ...d, objects: doc.objects, review: { ...d.review, user_declaration: null } }),
  });
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = 'Nesneler gruplandı.';
});

ungroupBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  const doc = branchWorkflow.getActiveDocument();
  const updated = doc.objects.map((o) =>
    state.selectedIds.has(o.id) ? { ...o, group_id: null, group: null } : o
  );
  branchWorkflow.applyCommand({
    description: `Ungroup ${state.selectedIds.size} objects`,
    execute: (d) => ({ ...d, objects: updated, review: { ...d.review, user_declaration: null } }),
    undo: (d) => ({ ...d, objects: doc.objects, review: { ...d.review, user_declaration: null } }),
  });
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = 'Grup çözüldü.';
});

// Delete
deleteBtn.addEventListener('click', async () => {
  if (!branchWorkflow || state.selectedIds.size === 0) return;
  const cmd = new DeleteObjectsCommand(Array.from(state.selectedIds));
  branchWorkflow.applyCommand(cmd);
  state.selectedIds.clear();
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = 'Nesneler silindi.';
});

// Undo / Redo
undoBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  branchWorkflow.undo();
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = 'Geri alındı.';
});

redoBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  branchWorkflow.redo();
  syncUIFromDocument(branchWorkflow.getActiveDocument());
  await autoSaveDraft();
  statusDiv.textContent = 'İleri alındı.';
});

// 6. Mask Erase & Rebuild Workflow (Branch Preservation)
// NOTE: tool routing is handled centrally by toolSelect's change listener
// (applyToolPointerRouting). The Dönüştür mask editing path stays below; in the
// Defter workspace the eraser now deletes vector objects via the pointer
// controller, so the mask canvas is not shown there.

let isErasing = false;

function eraseAt(clientX: number, clientY: number) {
  // Mask editing only exists in the Dönüştür workspace; in Defter the eraser
  // gesture belongs to the vector scene (pointer controller).
  if (isNotebookMode()) return;
  const rect = maskCanvas.getBoundingClientRect();
  const scaleX = maskCanvas.width > 0 && rect.width > 0 ? maskCanvas.width / rect.width : 1;
  const scaleY = maskCanvas.height > 0 && rect.height > 0 ? maskCanvas.height / rect.height : 1;
  const x = (clientX - rect.left) * scaleX;
  const y = (clientY - rect.top) * scaleY;

  const mctx = maskCanvas.getContext('2d');
  if (mctx) {
    mctx.fillStyle = '#ffffff'; // erase ink mask (white = background)
    mctx.beginPath();
    mctx.arc(x, y, (Number(widthInput.value) || 10) * 2, 0, Math.PI * 2);
    mctx.fill();
  }
  state.corrected = 'edited_mask';
  if (!state.automatic) {
    state.automatic = 'initial';
  }
}

function onEraseDown(clientX: number, clientY: number) {
  if (toolSelect.value !== 'erase' || isNotebookMode()) return;
  const rect = maskCanvas.getBoundingClientRect();
  if (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  ) {
    isErasing = true;
    eraseAt(clientX, clientY);
  }
}

function onEraseMove(clientX: number, clientY: number) {
  if (!isErasing || toolSelect.value !== 'erase') return;
  eraseAt(clientX, clientY);
}

function onEraseUp() {
  isErasing = false;
}

maskCanvas.addEventListener('pointerdown', (ev) => {
  if (isNotebookMode()) return;
  isErasing = true;
  eraseAt(ev.clientX, ev.clientY);
});
maskCanvas.addEventListener('mousedown', (ev) => {
  if (isNotebookMode()) return;
  isErasing = true;
  eraseAt(ev.clientX, ev.clientY);
});
maskCanvas.addEventListener('pointermove', (ev) => {
  if (isErasing && !isNotebookMode()) eraseAt(ev.clientX, ev.clientY);
});
maskCanvas.addEventListener('mousemove', (ev) => {
  if (isErasing && !isNotebookMode()) eraseAt(ev.clientX, ev.clientY);
});
maskCanvas.addEventListener('pointerup', onEraseUp);
maskCanvas.addEventListener('mouseup', onEraseUp);

window.addEventListener('pointerdown', (ev) => {
  onEraseDown(ev.clientX, ev.clientY);
});
window.addEventListener('mousedown', (ev) => {
  onEraseDown(ev.clientX, ev.clientY);
});
window.addEventListener('pointermove', (ev) => {
  onEraseMove(ev.clientX, ev.clientY);
});
window.addEventListener('mousemove', (ev) => {
  onEraseMove(ev.clientX, ev.clientY);
});
window.addEventListener('pointerup', () => {
  onEraseUp();
});
window.addEventListener('mouseup', () => {
  onEraseUp();
});

rebuildBtn.addEventListener('click', async () => {
  if (!branchWorkflow) return;
  setBusy(true);
  statusDiv.textContent = 'Düzeltilmiş maske yeniden vektörleştiriliyor...';

  try {
    const maskDataUrl = maskCanvas.toDataURL('image/png');
    const response = await fetch('/api/process', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image: maskDataUrl,
        mode: 'mask',
      }),
    });

    if (!response.ok) {
      throw new Error(`Re-vectorize failed: ${response.statusText}`);
    }

    const data = await response.json();
    const geo = data.geometry;
    const newObjects: CompoundFill[] = (geo.geometries || []).map((g: any, idx: number) => ({
      id: g.geometry_id || `geo_reb_${idx + 1}`,
      kind: 'compound_fill' as const,
      rings: g.rings.map((r: any) => ({
        points: r.points as [number, number][],
      })),
      fill_rule: (geo.fill_rule || 'evenodd') as 'evenodd' | 'nonzero',
      color: colorInput.value || '#163d58',
      opacity: 1.0,
      transform: [1, 0, 0, 1, 0, 0] as [number, number, number, number, number, number],
      source_kind: 'reconstructed' as const,
    }));

    const currentDoc = branchWorkflow.getActiveDocument();
    const newDoc: CIMDocument = {
      ...currentDoc,
      document_id: currentDoc.document_id,
      revision_id: `rev_reb_${Date.now()}`,
      objects: newObjects,
      review: {
        ...currentDoc.review,
        user_declaration: null,
      },
    };

    // Revectorize with branch preservation!
    if (branchWorkflow) {
      branchWorkflow.markDirty();
    }
    const res: RevectorizationResult = branchWorkflow.revectorize({
      newDocument: newDoc,
      description: 'Düzeltilmiş maskeden yeniden vektörleştirme',
    });

    toolSelect.value = 'select';
    maskCanvas.style.display = 'none';
    inkSvg.style.pointerEvents = 'auto';

    syncUIFromDocument(branchWorkflow.getActiveDocument());
    await autoSaveDraft();
    statusDiv.textContent = res.message;
  } catch (err: any) {
    statusDiv.textContent = `Vektörleştirme hatası: ${err.message}`;
  } finally {
    setBusy(false);
  }
});

// Branch switching
branchSelect.addEventListener('change', () => {
  if (!branchWorkflow) return;
  const branchId = branchSelect.value;
  branchWorkflow.switchBranch(branchId);
  syncUIFromDocument(branchWorkflow.getActiveDocument());
});

// Source toggle
sourceToggleBtn.addEventListener('click', () => {
  showSource = !showSource;
  (window as any).showSource = showSource;
  sourceCanvas.style.display = showSource ? 'block' : 'none';
  statusDiv.textContent = showSource ? 'Kaynak görsel gösteriliyor.' : 'Kaynak görsel gizlendi.';
});

// Source deletion via real UI button (AGY_PERSONAL_FINAL_UX)
if (deleteSourceBtn) {
  deleteSourceBtn.addEventListener('click', async () => {
    const docId = authLibrary ? authLibrary.getCurrentDocId() : null;
    const currentDoc = branchWorkflow ? branchWorkflow.getActiveDocument() : null;
    if (!docId || !currentDoc) {
      statusDiv.textContent = 'Aktif bir bulut belgesi bulunamadı.';
      return;
    }

    setBusy(true);
    try {
      statusDiv.textContent = 'Kaynak fotoğraf siliniyor...';
      const result = await executeSourceDeletion({
        documentId: docId,
        cimDocument: currentDoc,
        authLibrary,
      });

      if (!result.confirmed) {
        statusDiv.textContent = 'Kaynak fotoğraf silme işlemi iptal edildi.';
        return;
      }

      if (!result.deleted) {
        throw new Error(result.error || 'Kaynak silinemedi');
      }

      await autoSaveDraft();
      clearRenderedSource();
      statusDiv.textContent = 'Kaynak fotoğraf başarıyla silindi. Vektör düzenleme ve dışa aktarma etkin kalmaya devam ediyor.';
      updateSourceControls();
    } catch (err: any) {
      statusDiv.textContent = `Kaynak silme hatası: ${err.message}`;
    } finally {
      setBusy(false);
    }
  });
}

stage.parentElement?.addEventListener('wheel', (ev) => {
  if (!branchWorkflow) return;
  ev.preventDefault();
  stageZoom = clampZoom(stageZoom * (ev.deltaY < 0 ? 1.1 : 0.9));
  fitStageViewer();
}, { passive: false });

window.addEventListener('resize', () => {
  fitSourceViewer();
  fitStageViewer();
});

// 8. Export Pure Vector SVG & JSON
exportSvgBtn.addEventListener('click', () => {
  if (!branchWorkflow) return;
  const doc = branchWorkflow.getActiveDocument();
  const svgStr = exportToSVG(doc);

  // Trigger browser download
  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `paper2ink-${doc.document_id}.svg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  statusDiv.textContent = 'Saf vektör SVG dışa aktarıldı (rastır içermez).';
});

saveBtn.addEventListener('click', () => {
  if (!branchWorkflow) return;
  const doc = branchWorkflow.getActiveDocument();
  const exportPayload = {
    ...state,
    version: 'local-prototype-1',
    schema_version: 'cim-1.0',
    cim_document: doc,
  };
  const jsonStr = JSON.stringify(exportPayload);

  const blob = new Blob([jsonStr], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'saved.paper2ink.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  statusDiv.textContent = 'CIM v1 JSON belgesi kaydedildi.';
});

// ---------------------------------------------------------------------------
// Cloud Operations & Library (SAAS-312)
// ---------------------------------------------------------------------------

// 8b. Cloud Save
saveCloudBtn.addEventListener('click', async () => {
  if (!branchWorkflow) {
    statusDiv.textContent = 'Kaydedilecek aktif bir belge yok.';
    return;
  }
  const currentDoc = branchWorkflow.getActiveDocument();

  if (!authLibrary.isAuthenticated()) {
    statusDiv.textContent = 'Buluta kaydetmek için lütfen önce giriş yapın.';
    await authLibrary.preserveDraftOnExpired(currentDoc);
    return;
  }

  setBusy(true);
  try {
    let docId = authLibrary.getCurrentDocId();
    if (!docId) {
      const doc = await authLibrary.createDocument('Bulut Çizimi');
      docId = doc.id;
      currentDocumentTitle = doc.title;
      authLibrary.setCurrentDocId(docId);
    }

    if (!syncManager) {
      initSyncManager(docId, authLibrary.getCurrentHeadRevisionId());
    }

    if (syncManager) {
      const rev = await syncManager.saveToCloud({
        branchKind: state.activeBranch === 'main' ? 'manual' : 'mask_edit',
      });
      authLibrary.setCurrentHeadRevisionId(rev.id);
      statusDiv.textContent = `Belge buluta kaydedildi (Revizyon: ${rev.id.substring(0, 8)}).`;
    }
    sessionExpiredNotice.style.display = 'none';
  } catch (err: any) {
    if (err instanceof ApiClientError && err.status === 401) {
      statusDiv.textContent = 'Oturum süresi doldu. Taslağınız yerel hafızada saklandı.';
    } else if (err instanceof ApiClientError && err.status === 409) {
      statusDiv.textContent = 'Kaydetme çatışması (409): Belge sunucuda başka bir oturum tarafından güncellenmiş.';
    } else {
      statusDiv.textContent = `Buluta kaydetme hatası: ${err.message}`;
    }
  } finally {
    setBusy(false);
  }
});

// Library Dialog
openLibraryBtn.addEventListener('click', async () => {
  if (!authLibrary.isAuthenticated()) {
    statusDiv.textContent = 'Bulut kütüphanesini açmak için lütfen önce giriş yapın.';
    sessionExpiredNotice.style.display = 'flex';
    sessionExpiredMsg.textContent = 'Lütfen kütüphaneye erişmek için giriş yapın.';
    return;
  }
  libraryModal.style.display = 'flex';
  await refreshLibraryList();
});

closeLibraryBtn.addEventListener('click', () => {
  libraryModal.style.display = 'none';
});

createNewDocBtn.addEventListener('click', async () => {
  const title = newDocTitleInput.value.trim();
  if (!title) return;
  try {
    const doc = await authLibrary.createDocument(title);
    currentDocumentTitle = doc.title;
    newDocTitleInput.value = '';
    await refreshLibraryList();
    statusDiv.textContent = `"${doc.title}" başlıklı yeni bulut belgesi oluşturuldu.`;
  } catch (err: any) {
    statusDiv.textContent = `Belge oluşturma hatası: ${err.message}`;
  }
});

async function refreshLibraryList() {
  libraryDocList.innerHTML = '<p style="color: #666;">Belgeler yükleniyor...</p>';
  try {
    const resp = await authLibrary.listDocuments();
    if (!resp.items || resp.items.length === 0) {
      libraryDocList.innerHTML = '<p style="color: #666;">Kütüphanede henüz kayıtlı belge yok.</p>';
      return;
    }
    libraryDocList.innerHTML = '';
    for (const doc of resp.items) {
      const itemDiv = document.createElement('div');
      itemDiv.style.cssText = 'display: flex; justify-content: space-between; align-items: center; padding: 10px; border-bottom: 1px solid #eee;';

      const infoDiv = document.createElement('div');
      const titleEl = document.createElement('strong');
      titleEl.textContent = doc.title;
      const metaEl = document.createElement('div');
      metaEl.style.cssText = 'font-size: 11px; color: #777; margin-top: 2px;';
      metaEl.textContent = `ID: ${doc.id.substring(0, 8)} | Rev: ${doc.head_revision_id ? doc.head_revision_id.substring(0, 8) : 'Yok'} | Sürüm: ${doc.version}`;
      infoDiv.appendChild(titleEl);
      infoDiv.appendChild(metaEl);

      const btnGroup = document.createElement('div');
      btnGroup.style.cssText = 'display: flex; gap: 8px;';

      const openDocBtn = document.createElement('button');
      openDocBtn.textContent = 'Aç';
      openDocBtn.className = 'primary';
      openDocBtn.addEventListener('click', async () => {
        try {
          statusDiv.textContent = `"${doc.title}" yükleniyor...`;
          currentDocumentTitle = doc.title;
          const res = await authLibrary.openDocument(doc.id);
          if (res.cimDocument) {
            branchWorkflow = new BranchWorkflowManager(res.cimDocument);
            await syncUIFromDocument(res.cimDocument);
            initSyncManager(doc.id, doc.head_revision_id);
            await autoSaveDraft();
            statusDiv.textContent = `"${doc.title}" belgesi buluttan yüklendi (${res.cimDocument.objects.length} nesne).`;
          } else {
            // Revisionless (legacy/blank pending) page: open a valid empty
            // cim-1.1 canvas instead of a dead workflow (TN-06).
            branchWorkflow = new BranchWorkflowManager(
              createBlankCIMDocument(doc.id, `pending_${doc.id}`)
            );
            await syncUIFromDocument(branchWorkflow.getActiveDocument());
            initSyncManager(doc.id, doc.head_revision_id);
            statusDiv.textContent = `"${doc.title}" açıldı (boş sayfa; kalemle yazabilirsiniz).`;
          }
          updateSourceControls();
          libraryModal.style.display = 'none';
        } catch (err: any) {
          statusDiv.textContent = `Belge açma hatası: ${err.message}`;
        }
      });

      const delDocBtn = document.createElement('button');
      delDocBtn.textContent = 'Sil';
      delDocBtn.className = 'danger';
      delDocBtn.addEventListener('click', async () => {
        if (!confirm(`"${doc.title}" belgesini silmek istediğinize emin misiniz?`)) return;
        try {
          await authLibrary.deleteDocument(doc.id);
          await refreshLibraryList();
          statusDiv.textContent = `"${doc.title}" belgesi silindi.`;
        } catch (err: any) {
          statusDiv.textContent = `Belge silme hatası: ${err.message}`;
        }
      });

      btnGroup.appendChild(openDocBtn);
      btnGroup.appendChild(delDocBtn);
      itemDiv.appendChild(infoDiv);
      itemDiv.appendChild(btnGroup);
      libraryDocList.appendChild(itemDiv);
    }
  } catch (err: any) {
    libraryDocList.innerHTML = `<p style="color: #aa3333;">Kütüphane yüklenemedi: ${err.message}</p>`;
  }
}

// Fallback JSON Download on Expired Session
fallbackDownloadBtn.addEventListener('click', () => {
  if (!branchWorkflow) return;
  const doc = branchWorkflow.getActiveDocument();
  const jsonStr = exportToJSON(doc);
  const blob = new Blob([jsonStr], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `paper2ink-draft-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  statusDiv.textContent = 'Taslak JSON dosyası olarak indirildi.';
});

// Re-login button
reloginBtn.addEventListener('click', () => {
  window.location.href = '/api/v1/auth/login?return_path=' + encodeURIComponent(window.location.pathname);
});

// Auth Status Checking & Login/Logout
loginBtn.addEventListener('click', () => {
  window.location.href = '/api/v1/auth/login?return_path=' + encodeURIComponent(window.location.pathname);
});

logoutBtn.addEventListener('click', async () => {
  await authLibrary.logout();
  await checkAuthStatus();
});

async function checkAuthStatus() {
  if (window.location.hash && window.location.hash.includes('pairing_token=')) {
    const match = window.location.hash.match(/pairing_token=([^&]+)/);
    if (match) {
      const token = decodeURIComponent(match[1]);
      try {
        await fetch('/api/v1/auth/pair', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pairing_token: token, device_name: 'Mobile Browser' }),
        });
        history.replaceState(null, '', window.location.pathname);
      } catch (err) {
        console.warn('Pairing token exchange failed:', err);
      }
    }
  }

  const res = await authLibrary.checkAuth();
  if (res.authenticated && res.user) {
    authStatus.textContent = res.user.email || res.user.id;
    loginBtn.style.display = 'none';
    logoutBtn.style.display = 'inline-block';
    sessionExpiredNotice.style.display = 'none';
  } else {
    authStatus.textContent = 'Giriş Yapılmadı';
    loginBtn.style.display = 'inline-block';
    logoutBtn.style.display = 'none';
  }
}

// Open document transactionally
if (openBtn) {
  openBtn.addEventListener('click', () => {
    openFileInput.click();
  });
}

openFileInput.addEventListener('change', async () => {
  const file = openFileInput.files?.[0];
  if (!file) return;
  const text = await file.text();
  try {
    // VS-SLICE-1: accept .svg in the open picker too. Detection is by content
    // (leading '<' + svg root), not extension, so JSON behavior is unchanged.
    if (/^\s*<\?xml|^\s*<svg[\s>]/i.test(text) && /<svg[\s>]/i.test(text)) {
      await importSvgFile(file);
    } else {
      await handleOpenJson(text);
    }
  } catch (err: any) {
    statusDiv.textContent = `Açma hatası: ${err.message}`;
  }
  openFileInput.value = '';
});

async function handleOpenJson(jsonStr: string): Promise<boolean> {
  const parsed = JSON.parse(jsonStr);

  // Validate negative scale rejection per contract
  if (parsed.objects && Array.isArray(parsed.objects)) {
    for (const obj of parsed.objects) {
      if (typeof obj.scale === 'number' && (obj.scale <= 0 || !Number.isFinite(obj.scale))) {
        throw new Error(`Invalid scale: ${obj.scale}`);
      }
    }
  }

  let cimDoc: CIMDocument;
  if (parsed.cim_document) {
    validateCIMDocument(parsed.cim_document);
    cimDoc = parsed.cim_document;
  } else {
    const res = openDocument(jsonStr, branchWorkflow ? branchWorkflow.getActiveDocument() : undefined);
    cimDoc = res.document;
  }

  branchWorkflow = new BranchWorkflowManager(cimDoc);
  if (Array.isArray(parsed.branches)) {
    for (const bName of parsed.branches) {
      if (bName !== 'main') {
        branchWorkflow.createBranch(bName, bName.startsWith('mask_edit') ? 'mask_edit' : 'manual');
      }
    }
    const targetBranch = branchWorkflow.listBranches().find((b) => b.name === parsed.activeBranch);
    if (targetBranch) {
      branchWorkflow.switchBranch(targetBranch.id);
    }
  }

  currentDocumentTitle = (cimDoc as any).title || parsed.title || 'İçe Aktarılan Belge';
  syncUIFromDocument(cimDoc);

  // Restore state cleanly for full byte-for-byte serialization parity
  for (const k of Object.keys(state)) {
    if (k in parsed) {
      if (k === 'selectedIds') {
        state.selectedIds = new Set(Array.isArray(parsed.selectedIds) ? parsed.selectedIds : []);
      } else {
        (state as any)[k] = parsed[k];
      }
    }
  }

  await autoSaveDraft();
  statusDiv.textContent = `Belge başarıyla açıldı (${cimDoc.objects.length} nesne).`;
  return true;
}

// 9. IndexedDB Offline Recovery on Startup
async function initRecoveryCheck() {
  try {
    const drafts = await draftStore.listDrafts();
    const latestDraft = drafts.length > 0 ? drafts[0] : null;
    if (latestDraft && latestDraft.document) {
      recoveryNotice.style.display = 'flex';
      recoveryMsg.textContent = `Önceki oturumdan kurtarılabilir taslak bulundu (${latestDraft.document.objects.length} nesne).`;

      recoverDraftBtn.onclick = () => {
        branchWorkflow = new BranchWorkflowManager(latestDraft.document, 100, true);
        branchWorkflow.markDirty();
        syncUIFromDocument(latestDraft.document);
        recoveryNotice.style.display = 'none';
        state.draftRecovered = true;
        statusDiv.textContent = 'Yerel taslak IndexedDB üzerinden kurtarıldı.';
      };

      discardDraftBtn.onclick = async () => {
        await draftStore.deleteDraft(latestDraft.documentId);
        recoveryNotice.style.display = 'none';
        statusDiv.textContent = 'Önceki taslak silindi.';
      };
    }
  } catch (err) {
    console.warn('Draft recovery probe error:', err);
  }
}

initRecoveryCheck();
checkAuthStatus();

// 10. Background Conversion Job Resume Check (§8, SAAS-410)
// A job left over from a previous session must NOT open a modal on startup.
// The only surface is the small status button; opening the dialog is the
// user's explicit choice. Terminal jobs are cleared inside the manager.
jobManager.checkAndResumePendingJob().then((restoredJob) => {
  if (restoredJob) {
    if (jobStatusBtn) {
      jobStatusBtn.style.display = 'inline-block';
      jobStatusBtn.textContent = `İş: ${restoredJob.state}`;
    }
  }
}).catch((err) => {
  console.warn('Job resume probe error:', err);
});
